import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Typography,
    Box,
    Paper,
    Card,
    CardContent,
    LinearProgress,
    Button,
    Snackbar,
    Alert,
    useTheme,
    Divider,
    Chip
} from '@mui/material';
import BookIcon from '@mui/icons-material/Book';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FlagIcon from '@mui/icons-material/Flag';
import DiamondIcon from '@mui/icons-material/Diamond';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedIcon from '@mui/icons-material/Verified';
import PeopleIcon from '@mui/icons-material/People';
import TranslateIcon from '@mui/icons-material/Translate';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import api from '../services/api';

const RARITY_COLORS = {
    bronze: '#CD7F32',
    silver: '#C0C0C0',
    gold: '#FFD700',
    platinum: '#E5E4E2',
    diamond: '#B9F2FF'
};

const RARITY_LABELS = {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum',
    diamond: 'Diamond'
};

// Badge categories configuration — matches server/src/config/badges.js
const BADGE_CATEGORIES = {
    reading: {
        title: 'Reading Volume',
        icon: BookIcon,
        color: '#4CAF50',
        badges: ['firstWords', 'noviceReader', 'bookworm', 'literaryLion', 'wordDevourer', 'millionClub'],
        description: 'Read more words to unlock these'
    },
    speed: {
        title: 'Peak Speed',
        icon: BoltIcon,
        color: '#F44336',
        badges: ['quickStart', 'warmUp', 'speedster', 'fourDigitClub', 'velocityKing', 'supersonic', 'hyperdrive', 'warpSpeed', 'lightspeed', 'ludicrousSpeed'],
        description: 'Push your peak reading speed higher'
    },
    speedSustained: {
        title: 'Sustained Speed',
        icon: TrendingUpIcon,
        color: '#E91E63',
        badges: ['steadyPace', 'consistentCruiser', 'speedDemon', 'relentless', 'untouchable'],
        description: 'Maintain fast speeds over many articles'
    },
    speedComprehension: {
        title: 'Speed + Comprehension',
        icon: SpeedIcon,
        color: '#FF5722',
        badges: ['speedAndSmarts', 'quickThinker', 'speedReaderElite', 'lightningGenius', 'theImpossible'],
        description: 'Read fast AND understand what you read'
    },
    speedVolume: {
        title: 'Fast Reads',
        icon: VerifiedIcon,
        color: '#D32F2F',
        badges: ['fastFive', 'speedyTwenty', 'centurionSpeed', 'eliteFleet', 'speedLegacy'],
        description: 'Prove your speed consistently across articles'
    },
    comprehension: {
        title: 'Comprehension',
        icon: PsychologyIcon,
        color: '#9C27B0',
        badges: ['sharpMind', 'brainiac', 'perfectRecall', 'comprehensionAce', 'perfectStreak3', 'perfectStreak10'],
        description: 'Prove you understand what you read'
    },
    dedication: {
        title: 'Dedication',
        icon: EmojiEventsIcon,
        color: '#FF9800',
        badges: ['firstHour', 'fiveHours', 'twentyHours', 'fiftyHours', 'hundredHours'],
        description: 'Invest time in your reading journey'
    },
    consistency: {
        title: 'Quizzes',
        icon: SchoolIcon,
        color: '#2196F3',
        badges: ['quizRookie', 'quizVeteran', 'quizMaster', 'quizLegend', 'articleHunter'],
        description: 'Complete quizzes and read articles'
    },
    streaks: {
        title: 'Streaks',
        icon: LocalFireDepartmentIcon,
        color: '#FF5722',
        badges: ['threeDayStreak', 'weekWarrior', 'twoWeekTitan', 'monthlyMaster', 'hundredDays', 'yearStreak'],
        description: 'Keep your streak alive'
    },
    insights: {
        title: 'Exploration',
        icon: LightbulbIcon,
        color: '#00BCD4',
        badges: ['explorer', 'polymath', 'subjectFocused', 'subjectMaster', 'renaissanceReader'],
        description: 'Explore different subjects'
    },
    milestones: {
        title: 'Milestones',
        icon: FlagIcon,
        color: '#607D8B',
        badges: ['onboardingComplete', 'firstArticle', 'tenArticles', 'fiftyArticles', 'hundredArticles', 'fiveHundredArticles'],
        description: 'Hit key reading and onboarding milestones'
    },
    flex: {
        title: 'Flex Badges',
        icon: DiamondIcon,
        color: '#E91E63',
        badges: ['speedAndBrains', 'ironReader', 'theFlexBadge', 'comeback', 'earlyBird', 'socialReader', 'firstFlex', 'cardCollector', 'challenger', 'duelMaster', 'goingPublic', 'flexFeeder', 'groupLeader', 'boardClimber'],
        description: 'Rare achievements and social bragging rights'
    },
    affiliate: {
        title: 'Referrals',
        icon: PeopleIcon,
        color: '#00BCD4',
        badges: ['firstReferral', 'fiveReferrals', 'tenReferrals', 'twentyFiveRefs', 'fiftyReferrals', 'hundredReferrals'],
        description: 'Build the community through referrals'
    },
    language: {
        title: 'Language Learning',
        icon: TranslateIcon,
        color: '#3F51B5',
        badges: ['firstForeign', 'bilingual', 'trilingual', 'polyglot', 'hyperglot', 'worldReader', 'gradeClimber', 'foreignFluency'],
        description: 'Read in multiple languages and climb grade levels'
    },
    vocabulary: {
        title: 'Vocabulary',
        icon: MenuBookIcon,
        color: '#8BC34A',
        badges: ['wordCollector', 'wordHoarder', 'lexicon', 'flashcardRookie', 'flashcardPro', 'wordMaster'],
        description: 'Build your word bank and master flashcards'
    },
    mastery: {
        title: 'Mastery',
        icon: WorkspacePremiumIcon,
        color: '#673AB7',
        badges: ['collector', 'achiever', 'badgeHunter', 'badgeMaster', 'completionist'],
        description: 'Earn badges to unlock mastery'
    }
};

const BadgeCard = ({ badge, category, earned, progress }) => {
    const badgeTitle = badge?.title || 'Unknown Badge';
    const badgeDescription = badge?.description || 'Badge description unavailable';
    const badgeProgress = typeof progress === 'number' && !isNaN(progress) 
        ? Math.min(100, Math.max(0, progress)) 
        : (typeof badge?.progress === 'number' && !isNaN(badge.progress) 
            ? Math.min(100, Math.max(0, badge.progress)) 
            : 0);
    const badgeEarned = earned === true || badge?.earned === true;
    const badgeIcon = badge?.icon || '🏆';
    const badgeEarnedDate = badge?.earnedDate ? new Date(badge.earnedDate) : null;
    const rarity = badge?.rarity || 'bronze';
    const rarityColor = RARITY_COLORS[rarity] || RARITY_COLORS.bronze;
    const rarityLabel = RARITY_LABELS[rarity] || 'Bronze';

    return (
        <Card 
            sx={{ 
                height: '100%',
                opacity: badgeEarned ? 1 : 0.6,
                transition: 'all 0.3s ease',
                borderLeft: `4px solid ${badgeEarned ? rarityColor : '#e0e0e0'}`,
                position: 'relative',
                overflow: 'visible',
                background: badgeEarned 
                    ? `linear-gradient(135deg, ${rarityColor}08 0%, transparent 60%)` 
                    : undefined,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: badgeEarned 
                        ? `0 4px 16px ${rarityColor}40`
                        : '0px 2px 4px rgba(0,0,0,0.15)'
                }
            }}
        >
            {badgeEarned && (
                <Chip
                    label={rarityLabel}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: 10,
                        backgroundColor: rarityColor,
                        color: rarity === 'diamond' || rarity === 'platinum' ? '#333' : '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem'
                    }}
                />
            )}
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography 
                        sx={{ 
                            fontSize: 36,
                            mr: 1.5,
                            filter: badgeEarned ? `drop-shadow(0 0 6px ${rarityColor})` : 'grayscale(0.8)',
                            lineHeight: 1
                        }}
                    >
                        {badgeIcon}
                    </Typography>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {badgeTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {badgeDescription}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ mt: 1 }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={badgeProgress} 
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: badgeEarned ? rarityColor : category.color
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            {badgeEarned ? 'Complete!' : `${Math.round(badgeProgress)}%`}
                        </Typography>
                        {!badgeEarned && (
                            <Chip label={rarityLabel} size="small" variant="outlined"
                                sx={{ height: 18, fontSize: '0.65rem', borderColor: rarityColor, color: rarityColor }} />
                        )}
                    </Box>
                    {badgeEarnedDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                            Earned {badgeEarnedDate.toLocaleDateString()}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const CategoryCard = ({ category, badges, selected, onClick }) => {
    const IconComponent = category.icon;
    
    // Calculate earned badges count
    const earnedCount = badges ? Object.values(badges).filter(badge => badge.earned).length : 0;
    const totalCount = category.badges.length;
    const progress = totalCount > 0 ? Math.round((earnedCount / totalCount) * 100) : 0;
    
    return (
        <Card 
            sx={{ 
                cursor: 'pointer',
                height: '100%',
                borderLeft: selected ? `4px solid ${category.color}` : 'none',
                backgroundColor: selected ? `${category.color}10` : 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                }
            }}
            onClick={onClick}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <IconComponent sx={{ color: category.color, fontSize: 30, mr: 1 }} />
                    <Typography variant="h6">{category.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {category.description}
                </Typography>
                <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: category.color
                        }
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {earnedCount} of {totalCount} earned
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        {progress}%
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const Achievements = () => {
    const [badges, setBadges] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('reading');
    const [notification, setNotification] = useState(null);
    
    useEffect(() => {
        fetchBadges();
        
        // Set up event listener for badge updates
        const handleBadgeUpdate = () => {
            fetchBadges();
        };
        
        window.addEventListener('badgeUpdate', handleBadgeUpdate);
        
        return () => {
            window.removeEventListener('badgeUpdate', handleBadgeUpdate);
        };
    }, []);

    const fetchBadges = async () => {
        try {
            const response = await api.get('/users/badges');
            
            if (response.data.badges) {
                // Ensure all badge categories exist
                const badgeData = { ...response.data.badges };
                
                // Initialize any missing categories
                Object.keys(BADGE_CATEGORIES).forEach(category => {
                    if (!badgeData[category]) {
                        badgeData[category] = {};
                    }
                    
                    // Initialize any missing badges in this category with default values
                    BADGE_CATEGORIES[category].badges.forEach(badgeId => {
                        if (!badgeData[category][badgeId]) {
                            badgeData[category][badgeId] = {
                                title: `${badgeId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}`,
                                description: 'Keep practicing to unlock this badge',
                                earned: false,
                                progress: 0,
                                icon: '🏆'
                            };
                        } else {
                            // Ensure earned is a boolean
                            badgeData[category][badgeId].earned = badgeData[category][badgeId].earned === true;
                            
                            // Ensure progress is a number
                            if (typeof badgeData[category][badgeId].progress !== 'number' || isNaN(badgeData[category][badgeId].progress)) {
                                badgeData[category][badgeId].progress = 0;
                            } else {
                                badgeData[category][badgeId].progress = Math.round(
                                    Math.min(100, Math.max(0, badgeData[category][badgeId].progress))
                                );
                            }
                        }
                    });
                });
                
                setBadges(badgeData);
                
                // Check for newly earned badges and show notification
                if (response.data.newlyEarned && response.data.newlyEarned.length > 0) {
                    // Show the most recent badge earned
                    const latestBadge = response.data.newlyEarned[0];
                    setNotification({
                        title: latestBadge.title,
                        category: BADGE_CATEGORIES[latestBadge.category]?.title || latestBadge.category,
                        icon: latestBadge.icon
                    });
                }
            } else {
                console.log('No badges data in response');
                setBadges({});
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching badges:', error);
            setLoading(false);
            setBadges({});
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Typography>Loading achievements...</Typography>
            </Box>
        );
    }

    // Calculate overall progress
    const totalBadges = Object.values(BADGE_CATEGORIES).reduce((acc, cat) => acc + cat.badges.length, 0);
    const earnedBadges = Object.entries(badges).reduce((acc, [category, categoryBadges]) => {
        return acc + Object.values(categoryBadges).filter(badge => badge.earned).length;
    }, 0);
    const overallProgress = totalBadges > 0 ? Math.round((earnedBadges / totalBadges) * 100) : 0;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Notification */}
            <Snackbar
                open={notification !== null}
                autoHideDuration={3000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setNotification(null)} 
                    severity="success"
                    sx={{ 
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {notification?.icon && (
                            <Typography sx={{ mr: 1, fontSize: '1.5rem' }}>
                                {notification.icon}
                            </Typography>
                        )}
                        <Box>
                            <Typography variant="subtitle1">
                                New Badge Unlocked!
                            </Typography>
                            <Typography variant="body2">
                                {notification?.title} in {notification?.category}
                            </Typography>
                        </Box>
                    </Box>
                </Alert>
            </Snackbar>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Your Achievements
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                    Track your progress and earn badges as you improve your reading skills!
                </Typography>
                
                {/* Overall Progress */}
                <Paper sx={{ p: 3, mt: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Overall Progress</Typography>
                        <Chip 
                            label={`${earnedBadges} of ${totalBadges} badges earned`}
                            color="primary"
                        />
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={overallProgress} 
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            mb: 1
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            {overallProgress}% Complete
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* Main Content */}
            <Grid container spacing={3}>
                {/* Categories Sidebar */}
                <Grid item xs={12} md={4}>
                    <Typography variant="h6" gutterBottom sx={{ pl: 1 }}>
                        Badge Categories
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.entries(BADGE_CATEGORIES).map(([key, category]) => (
                            <CategoryCard 
                                key={key}
                                category={category}
                                badges={badges[key]}
                                selected={selectedCategory === key}
                                onClick={() => handleCategoryChange(key)}
                            />
                        ))}
                    </Box>
                </Grid>

                {/* Badges Display */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            {React.createElement(BADGE_CATEGORIES[selectedCategory].icon, { 
                                style: { 
                                    color: BADGE_CATEGORIES[selectedCategory].color,
                                    fontSize: 28,
                                    marginRight: 8
                                } 
                            })}
                            <Box>
                                <Typography variant="h5">
                                    {BADGE_CATEGORIES[selectedCategory].title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {BADGE_CATEGORIES[selectedCategory].description}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={3}>
                            {BADGE_CATEGORIES[selectedCategory].badges.map(badgeId => {
                                
                                // Check if the badge exists in the data
                                const badge = badges[selectedCategory]?.[badgeId];
                                
                                // If badge doesn't exist, create a default badge object
                                const badgeData = badge || {
                                    title: badgeId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                                    description: 'Keep practicing to unlock this badge',
                                    earned: false,
                                    progress: 0,
                                    icon: '📚'
                                };
                                
                                // Ensure progress is a number
                                const progress = typeof badgeData.progress === 'number' && !isNaN(badgeData.progress)
                                    ? Math.min(100, Math.max(0, badgeData.progress))
                                    : 0;
                                
                                return (
                                    <Grid item xs={12} sm={6} key={badgeId}>
                                        <BadgeCard
                                            badge={badgeData}
                                            category={BADGE_CATEGORIES[selectedCategory]}
                                            earned={badgeData.earned === true}
                                            progress={progress}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                        
                        {(!badges[selectedCategory] || Object.keys(badges[selectedCategory]).length === 0) && (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="h6" gutterBottom>
                                    No achievements yet in this category
                                </Typography>
                                <Typography color="text.secondary" sx={{ mb: 2 }}>
                                    Keep practicing to earn badges in {BADGE_CATEGORIES[selectedCategory].title}!
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    onClick={fetchBadges}
                                >
                                    Refresh Badges
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Achievements;
